import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import * as socialApi from '@/api/social';
import type { ReportReason } from '@/api/social';

interface ReportPostModalProps {
  isOpen: boolean;
  postId: string;
  onClose: () => void;
}

const REASONS: { value: ReportReason; label: string }[] = [
  { value: 'spam', label: 'Spam' },
  { value: 'inappropriate', label: 'Inappropriate' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'misinformation', label: 'Misinformation' },
  { value: 'offensive', label: 'Offensive' },
  { value: 'other', label: 'Other' },
];

export function ReportPostModal({ isOpen, postId, onClose }: ReportPostModalProps) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Please select a reason');
      return;
    }
    try {
      setSubmitting(true);
      await socialApi.reportPost(postId, {
        reason,
        description: description.trim() || undefined,
      });
      toast.success('Report submitted. Our team will review it.');
      setReason(null);
      setDescription('');
      onClose();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.info('You have already reported this post');
        onClose();
      } else {
        toast.error('Failed to submit report. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center px-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Modal — compact, centered, max-height constrained, scrollable body */}
      <div
        className="relative bg-white dark:bg-[#1C1B22] rounded-2xl shadow-2xl overflow-hidden"
        style={{ width: '340px', maxWidth: 'calc(100vw - 32px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — slim */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/8">
          <h3
            className="text-[15px] font-bold text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            Report Post
          </h3>
          <button
            onClick={onClose}
            className="p-1 -mr-1 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4 text-gray-400 dark:text-white/50" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="max-h-[50vh] overflow-y-auto px-4 py-3">
          <p
            className="text-xs text-gray-400 dark:text-white/50 mb-2.5"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Select a reason
          </p>

          {/* Reason chips — compact grid */}
          <div className="grid grid-cols-2 gap-1.5 mb-3">
            {REASONS.map((r) => (
              <button
                key={r.value}
                onClick={() => setReason(r.value)}
                className={`px-3 py-2 rounded-lg text-[13px] font-medium transition-all text-center ${
                  reason === r.value
                    ? 'bg-red-500 text-white shadow-sm shadow-red-200 dark:shadow-none'
                    : 'bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-white/70 hover:bg-gray-100 dark:hover:bg-white/10'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* Optional description — compact */}
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Additional details (optional)"
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-gray-150 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-white/30 text-[13px] resize-none focus:outline-none focus:ring-1 focus:ring-red-400/50 focus:border-red-400/50"
            style={{ fontFamily: 'Inter, sans-serif' }}
          />
        </div>

        {/* Footer — slim with single submit button */}
        <div className="px-4 py-3 border-t border-gray-100 dark:border-white/8 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg text-[13px] font-semibold text-gray-500 dark:text-white/60 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reason || submitting}
            className="flex-1 py-2 rounded-lg text-[13px] font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
